// src/components/EditProduct/EditProduct.jsx

import React, { useEffect, useState } from "react";
import {
  AiOutlinePlusCircle,
  AiOutlineDelete, 
} from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import {
  getProduct,
  updateProduct,
  clearErrors,
  getAllProducts,
} from "../../redux/actions/product";
import { toast } from "react-toastify";
import { categoriesData } from "../../static/data";
import imageCompression from "browser-image-compression";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate, useParams } from "react-router-dom";

const EditProduct = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasVariants, setHasVariants] = useState(false);
const [variantTypes, setVariantTypes] = useState([]);
const [variantItems, setVariantItems] = useState({});
const [variantStock, setVariantStock] = useState({});
const [stock, setStock] = useState("");
const [stockAvailability, setStockAvailability] = useState("All time stock available");



  const { product, isLoading, error, success } = useSelector(
    (state) => state.products
  );

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    tags: "",
    serviceableLocation: "more_than_1000kms",
    stock: "",
    gstPercentage: "",
    minimumOrderQuantity: 1,
    hsnCode: "",
    leadTime: "",
    pricePerQty: "Nos",
    quantitySlabs: [
      {
        min: 1,
        max: "",
        originalPrice: "",
        discountPrice: "",
        totalPrice: "",
      },
    ],
    existingImages: [], 
    imagesToRemove: [], 
    newImages: [],
    newImagePreviews: [], 
    orderedImages: [], 
    customPricePerQty: "",
    stockAvailability: "All time stock available",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Reset success and error states when the component mounts
    dispatch(clearErrors());

    // Fetch the product details
    dispatch(getProduct(id));

    return () => {
      // Clear errors when the component unmounts
      dispatch(clearErrors());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (product) {
      const predefinedOptions = [
        "Nos",
        "Kilogram",
        "Gram",
        "Meter",
        "Millimeter",
        "Centimeter",
        "Foot",
        "Square foot",
        "Litre",
        "Millilitre",
        "Cubic meter",
        "Dozen",
      ];
   
      setFormData((prevData) => ({
        ...prevData,
        name: product.name || "",
        description: product.description || "",
        category: product.category || "",
        tags: product.tags || "",
        serviceableLocation: product.serviceableLocation || "more_than_1000kms",
        stock: product.stock || "",
        stockAvailability: product.stockAvailability || "All time stock available",
        gstPercentage: product.gstPercentage || "",
        minimumOrderQuantity: product.minimumOrderQuantity || 1,
        hsnCode: product.hsnCode || "",
        leadTime: product.leadTime || "",
        pricePerQty: predefinedOptions.includes(product.pricePerQty)
          ? product.pricePerQty
          : "Others",
        customPricePerQty: predefinedOptions.includes(product.pricePerQty)
          ? ""
          : product.pricePerQty, // Set custom value if "Others"
        quantitySlabs:
          product.quantitySlabs.length > 0
            ? product.quantitySlabs
            : [
                {
                  min: 1,
                  max: "",
                  originalPrice: "",
                  discountPrice: "",
                  totalPrice: "",
                },
              ],
        existingImages: product.images || [],
        orderedImages: product.images || [],
      }));
    setHasVariants(product.hasVariants || false);
    setVariantTypes(product.variantTypes || []);
    setVariantItems(product.variantItems || {});
    // Convert variantStock array to object
    const variantStockObject = {};
product.variantStock.forEach((variant) => {
  variantStockObject[variant.combination] = {
    availability: variant.availability,
    stock: variant.stock,
  };
});
setVariantStock(variantStockObject);
    }
  }, [product]);
  

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearErrors());
    }

    if (success && isSubmitting) {
      toast.success("Product updated successfully!");
      dispatch(getAllProducts());
      navigate("/dashboard");
      dispatch(clearErrors());
      setIsSubmitting(false);
    }
  }, [error, success, navigate, dispatch, isSubmitting]);


const handleChange = (e) => {
  const { name, value } = e.target;

  // 1) If user is editing a specific slab field:
  if (name.startsWith("quantitySlabs")) {
    const [_, index, field] = name.split(".");
    const updatedQuantitySlabs = [...formData.quantitySlabs];
    updatedQuantitySlabs[index][field] = value;

    // Recalculate totalPrice if discountPrice changed
    if (field === "discountPrice") {
      const discountPrice = parseFloat(updatedQuantitySlabs[index].discountPrice) || 0;
      const gstPercentageNumber = parseFloat(formData.gstPercentage) || 0;
      const gstAmount = (discountPrice * gstPercentageNumber) / 100;
      updatedQuantitySlabs[index].totalPrice = (discountPrice + gstAmount).toFixed(2);
    }

    setFormData((prevData) => ({
      ...prevData,
      quantitySlabs: updatedQuantitySlabs,
    }));
  } 
  
  // 2) If the user changes the top-level gstPercentage:
  else if (name === "gstPercentage") {
    // Recalculate totalPrice for ALL quantity slabs
    const newGstValue = parseFloat(value) || 0;
    const updatedQuantitySlabs = formData.quantitySlabs.map((slab) => {
      const discountPrice = parseFloat(slab.discountPrice) || 0;
      const gstAmount = (discountPrice * newGstValue) / 100;
      return {
        ...slab,
        totalPrice: (discountPrice + gstAmount).toFixed(2),
      };
    });

    setFormData((prevData) => ({
      ...prevData,
      gstPercentage: value,
      quantitySlabs: updatedQuantitySlabs,
    }));
  } 

  // 3) Otherwise, handle normal field updates
  else {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }
};


  // Function to handle adding a new variant type
  const handleAddVariantType = () => {
    setVariantTypes([...variantTypes, ""]);
  };
  
  // Function to handle removing a variant type
  const handleRemoveVariantType = (index) => {
    const newVariantTypes = [...variantTypes];
    const removedType = newVariantTypes.splice(index, 1)[0];
    setVariantTypes(newVariantTypes);
  
    // Remove associated variant items
    const newVariantItems = { ...variantItems };
    delete newVariantItems[removedType];
    setVariantItems(newVariantItems);
  };
  
  // Function to handle changing a variant type
  const handleVariantTypeChange = (index, value) => {
    const newVariantTypes = [...variantTypes];
    newVariantTypes[index] = value;
    setVariantTypes(newVariantTypes);
  };
  
  // Function to handle adding a variant item
  const handleAddVariantItem = (variantType) => {
    const items = variantItems[variantType] || [];
    setVariantItems({
      ...variantItems,
      [variantType]: [...items, ""],
    });
  };
  
  // Function to handle removing a variant item
  const handleRemoveVariantItem = (variantType, idx) => {
    const items = variantItems[variantType];
    const newItems = items.filter((_, i) => i !== idx);
    setVariantItems({
      ...variantItems,
      [variantType]: newItems,
    });
  };
  
  // Function to handle changing a variant item
  const handleVariantItemChange = (variantType, idx, value) => {
    const items = variantItems[variantType];
    items[idx] = value;
    setVariantItems({
      ...variantItems,
      [variantType]: items,
    });
  };
  
  // Function to generate all combinations of variant items
  const generateVariantCombinations = () => {
    const variantTypesArray = variantTypes;
    const variantItemsArray = variantTypesArray.map(
      (type) => variantItems[type] || []
    );
    if (variantItemsArray.some((items) => items.length === 0)) {
      return [];
    }
    const combinations = cartesianProduct(...variantItemsArray);
    return combinations.map((combination) =>
      combination.map((value, index) => ({
        variantType: variantTypesArray[index],
        value,
      }))
    );
  };
  
  // Helper function to compute cartesian product
  function cartesianProduct(...arrays) {
    return arrays.reduce(
      (acc, curr) =>
        acc
          .map((x) => curr.map((y) => x.concat([y])))
          .reduce((a, b) => a.concat(b), []),
      [[]]
    );
  }
  
  // Function to handle variant stock change
  const handleVariantStockChange = (combination, data) => {
    const key = getCombinationKey(combination);
    setVariantStock({
      ...variantStock,
      [key]: data,
    });
  };
  
  // Helper function to get a unique key for a combination
  const getCombinationKey = (combination) => {
    return combination
      .map((item) => `${item.variantType}:${item.value}`)
      .join("|");
  };
  // Handle removal of existing images
  const handleRemoveExistingImage = (public_id) => {
    setFormData((prevData) => ({
      ...prevData,
      existingImages: prevData.existingImages.filter(
        (img) => img.public_id !== public_id
      ),
      imagesToRemove: [...prevData.imagesToRemove, public_id],
    }));
  };

  // Handle addition of new images with compression and size validation
  const handleNewImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedImages = [];

    for (let file of files) {
      try {
        // Compress the image
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);

        // Validate compressed image size
        if (compressedFile.size / 1024 / 1024 > 1) {
          toast.error(`${file.name} is larger than 1MB after compression.`);
          continue;
        }

        // Create a preview URL
        const preview = URL.createObjectURL(compressedFile);

        compressedImages.push({ file: compressedFile, preview });
      } catch (error) {
        toast.error(`Error compressing ${file.name}: ${error.message}`);
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      newImages: [...prevData.newImages, ...compressedImages.map((img) => img.file)],
      newImagePreviews: [
        ...prevData.newImagePreviews,
        ...compressedImages.map((img) => img.preview),
      ],
    }));
  };

  // Handle removal of new images before submission
  const handleRemoveNewImage = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      newImages: prevData.newImages.filter((_, i) => i !== index),
      newImagePreviews: prevData.newImagePreviews.filter((_, i) => i !== index),
    }));
  };

  // Handle Drag End for Rearranging Existing Images
  const handleOnDragEndExistingImages = (result) => {
    if (!result.destination) return;

    const reorderedImages = Array.from(formData.existingImages);
    const [movedImage] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, movedImage);

    setFormData((prevData) => ({
      ...prevData,
      existingImages: reorderedImages,
      orderedImages: [...reorderedImages, ...formData.newImages],
    }));
  };

  // Handle Drag End for Rearranging New Images
  const handleOnDragEndNewImages = (result) => {
    if (!result.destination) return;

    const reorderedPreviews = Array.from(formData.newImagePreviews);
    const [movedPreview] = reorderedPreviews.splice(result.source.index, 1);
    reorderedPreviews.splice(result.destination.index, 0, movedPreview);

    const reorderedFiles = Array.from(formData.newImages);
    const [movedFile] = reorderedFiles.splice(result.source.index, 1);
    reorderedFiles.splice(result.destination.index, 0, movedFile);

    setFormData((prevData) => ({
      ...prevData,
      newImagePreviews: reorderedPreviews,
      newImages: reorderedFiles,
      orderedImages: [...formData.existingImages, ...reorderedFiles],
    }));
  };

  const handleQuantitySlabChange = (index, field, value) => {
    const newSlabs = formData.quantitySlabs.map((slab, i) => {
      if (i === index) {
        return { ...slab, [field]: value };
      }
      return slab;
    });

    if (field === "discountPrice" || field === "gstPercentage") {
      const discountPriceNumber = parseFloat(newSlabs[index].discountPrice) || 0;
      const gstPercentageNumber = parseFloat(formData.gstPercentage) || 0;
      const gstAmount = (discountPriceNumber * gstPercentageNumber) / 100;
      newSlabs[index].totalPrice = (discountPriceNumber + gstAmount).toFixed(2);
    }

    setFormData((prevData) => ({
      ...prevData,
      quantitySlabs: newSlabs,
    }));
  };

  const addQuantitySlab = () => {
    const lastSlab = formData.quantitySlabs[formData.quantitySlabs.length - 1];
    const newMin = lastSlab.max ? parseInt(lastSlab.max) + 1 : lastSlab.min + 1;

    setFormData((prevData) => ({
      ...prevData,
      quantitySlabs: [
        ...prevData.quantitySlabs,
        {
          min: newMin,
          max: "",
          originalPrice: "",
          discountPrice: "",
          totalPrice: "",
        },
      ],
    }));
  };

  const removeQuantitySlab = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      quantitySlabs: prevData.quantitySlabs.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    if (
      !formData.name ||
      !formData.description ||
      !formData.category ||
      (!hasVariants && formData.stockAvailability === "Limited stock available" && !formData.stock) ||
      formData.quantitySlabs.some(
        (slab) => !slab.max || !slab.originalPrice || !slab.discountPrice
      ) ||
      (formData.pricePerQty === "Others" && !formData.customPricePerQty)
    ) {
      toast.error("Please fill in all required fields.");
      return false;
    }
  
    if (parseFloat(formData.gstPercentage) < 0 || parseFloat(formData.gstPercentage) > 100) {
      toast.error("GST percentage must be between 0 and 100.");
      return false;
    }
  
    // Validate variant stocks
    if (hasVariants) {
      for (const [key, data] of Object.entries(variantStock)) {
        if (!data.availability) {
          toast.error(`Please select stock availability for variant combination: ${key}`);
          return false;
        }
        if (
          data.availability === "Limited stock available" &&
          (!data.stock || parseInt(data.stock, 10) <= 0)
        ) {
          toast.error(`Please enter a valid stock quantity for variant combination: ${key}`);
          return false;
        }
      }
    }
  
    return true;
  };
  

  const handleSubmit = (e) => {
    e.preventDefault();
  
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const finalPricePerQty =
      formData.pricePerQty === "Others"
        ? formData.customPricePerQty
        : formData.pricePerQty;
  
    // Prepare FormData to handle file uploads 
    const data = new FormData();
    data.set("name", formData.name);
    data.set("description", formData.description);
    data.set("category", formData.category);
    data.set("tags", formData.tags);
    data.set("serviceableLocation", formData.serviceableLocation);
    if (hasVariants) {
      const variantStockArray = Object.entries(variantStock).map(
        ([combinationKey, data]) => ({
          combination: combinationKey,
          availability: data.availability,
          stock: Number(data.stock),
        })
      );
      data.append("hasVariants", true);
      data.append("variantTypes", JSON.stringify(variantTypes));
      data.append("variantItems", JSON.stringify(variantItems));
      data.append("variantStock", JSON.stringify(variantStockArray));
  
      // Calculate total stock as sum of variant stocks
      const totalStock = variantStockArray.reduce(
        (sum, variant) => sum + variant.stock,
        0
      );
      data.append("stock", totalStock);
    }  else {
      if (formData.stockAvailability === "Limited stock available") {
        data.append("stock", formData.stock);
      } else {
        data.append("stock", 0); // Stock is not relevant for other options
      }
      data.append("stockAvailability", formData.stockAvailability); // Add this line
  
      // Ensure variant fields are cleared
      data.append("hasVariants", false);
      data.append("variantTypes", JSON.stringify([]));
      data.append("variantItems", JSON.stringify({}));
      data.append("variantStock", JSON.stringify([]));
    }
  
    data.set("gstPercentage", formData.gstPercentage);
    data.set("minimumOrderQuantity", formData.minimumOrderQuantity);
    data.set("hsnCode", formData.hsnCode);
    data.set("leadTime", formData.leadTime);
    data.set("pricePerQty", finalPricePerQty);
    data.set("quantitySlabs", JSON.stringify(formData.quantitySlabs));
    data.set("imagesToRemove", JSON.stringify(formData.imagesToRemove));

    // Append new images
    formData.newImages.forEach((file) => {
      data.append("images", file);
    });

    // Send ordered images if rearranged
    data.set("orderedImages", JSON.stringify(formData.existingImages));

    dispatch(updateProduct(id, data));
  };

  return (
    <div className="w-[90%] 800px:w-[50%] bg-white shadow h-[80vh] rounded-[4px] p-3 overflow-y-scroll">
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-lg font-semibold">Loading please wait Don't navigate away...</p>
        </div>
      ) : (
        <>
    <h5 className="text-[30px] font-Poppins text-center">
        Edit Product
      </h5>
      <form onSubmit={handleSubmit}>
        <br />
        {/* Name */}
        <div>
          <label className="pb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product name"
            required
          />
        </div>
        <br />
        {/* Description */}
        <div>
          <label className="pb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            cols="30"
            required
            rows="8"
            name="description"
            value={formData.description}
            className="mt-2 appearance-none block w-full pt-2 px-3 border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product description"
          ></textarea>
        </div>
        <br />
        {/* Category */}
        <div>
          <label className="pb-2">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full mt-2 border h-[35px] rounded-[5px]"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          >
            <option value="">Choose a category</option>
            {categoriesData.map((i) => (
              <option value={i.title} key={i.title}>
                {i.title}
              </option>
            ))}
          </select>
        </div>
        <br />
            {/* Product Variants */}
<div>
  <label className="pb-2">
    Product Variants <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    <label>
      <input
        type="radio"
        name="hasVariants"
        value="no"
        checked={!hasVariants}
        onChange={() => setHasVariants(false)}
      />{" "}
      No
    </label>
    <label className="ml-4">
      <input
        type="radio"
        name="hasVariants"
        value="yes"
        checked={hasVariants}
        onChange={() => setHasVariants(true)}
      />{" "}
      Yes
    </label>
  </div>
</div>

{hasVariants && (
  <>
    <br />
    {/* Variant Types */}
<div className="mt-6">
  <label className="pb-2 font-semibold text-lg">
    Variant Types <span className="text-red-500">*</span>
  </label>
  {variantTypes.map((variantType, index) => (
    <div key={index} className="flex items-center mt-2">
      <input
        type="text"
        value={variantType}
        onChange={(e) => handleVariantTypeChange(index, e.target.value)}
        className="w-full px-3 h-[35px] border border-gray-300 rounded-l-[3px]"
        placeholder="Enter variant type (e.g., Size, Color)"
      />
      <button
        type="button"
        className="ml-1 px-3 py-1 bg-red-500 text-white rounded-r-[3px]"
        onClick={() => handleRemoveVariantType(index)}
      >
        Remove
      </button>
    </div>
  ))}
  <button
    type="button"
    className="mt-2 px-4 py-2 bg-green-500 text-white rounded"
    onClick={handleAddVariantType}
  >
    Add Variant Type
  </button>
</div>

{/* Variant Items */}
{variantTypes.map((variantType, index) => (
  <div key={index} className="mt-6">
    <label className="pb-2 font-semibold text-lg">
      {variantType} Items <span className="text-red-500">*</span>
    </label>
    {(variantItems[variantType] || []).map((item, idx) => (
      <div key={idx} className="flex items-center mt-2">
        <input
          type="text"
          value={item}
          onChange={(e) =>
            handleVariantItemChange(variantType, idx, e.target.value)
          }
          className="w-full px-3 h-[35px] border border-gray-300 rounded-l-[3px]"
          placeholder={`Enter ${variantType} item`}
        />
        <button
          type="button"
          className="ml-1 px-3 py-1 bg-red-500 text-white rounded-r-[3px]"
          onClick={() => handleRemoveVariantItem(variantType, idx)}
        >
          Remove
        </button>
      </div>
    ))}
    <button
      type="button"
      className="mt-2 px-4 py-2 bg-green-500 text-white rounded"
      onClick={() => handleAddVariantItem(variantType)}
    >
      Add {variantType} Item
    </button>
  </div>
))}


    <br />
    {/* Variant Stock */}
{/* Variant Stock */}
<div className="mt-6">
  <label className="pb-2 font-semibold text-lg">
    Variant Stock <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    {generateVariantCombinations().map((combination, index) => {
      const combinationKey = getCombinationKey(combination);
      const variantStockEntry = variantStock[combinationKey] || {};

      return (
        <div
          key={index}
          className="border-b border-gray-200 pb-4 mb-4"
        >
          <div className="flex items-center">
            <span className="w-full">
              {combination
                .map((item) => `${item.variantType}: ${item.value}`)
                .join(", ")}
            </span>
          </div>
          <div className="mt-2">
            <label className="mr-4">
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="All time stock available"
                checked={
                  variantStockEntry.availability ===
                  "All time stock available"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              All time stock available
            </label>
            <label className="mr-4">
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="Limited stock available"
                checked={
                  variantStockEntry.availability ===
                  "Limited stock available"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              Limited stock available
            </label>
            <label>
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="Out of stock"
                checked={
                  variantStockEntry.availability === "Out of stock"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              Out of stock
            </label>
          </div>

          {/* If Limited stock is selected, show the stock quantity input */}
          {variantStockEntry.availability === "Limited stock available" && (
            <div className="mt-2">
              <label>Stock Quantity</label>
              <input
                type="number"
                value={variantStockEntry.stock || ""}
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    stock: parseInt(e.target.value, 10),
                  })
                }
                className="w-20 px-3 h-[35px] border border-gray-300 rounded"
                placeholder="Stock"
              />
            </div>
          )}
        </div>
      );
    })}
  </div>
</div>


  </>
)}


        {/* Tags */}
        <div>
          <label className="pb-2">Make</label>
          <input
            type="text"
            name="tags"
            value={formData.tags}
            className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product tags"
          />
        </div>
        {/* In src/components/EditProduct/EditProduct.jsx */}
<div>
  <label className="pb-2">
    Serviceable Area <span className="text-red-500">*</span>
  </label>
  <select
    className="w-full mt-2 border h-[35px] rounded-[5px]"
    name="serviceableLocation"
    value={formData.serviceableLocation}
    onChange={handleChange}
  >
    <option value="more_than_1000kms">More than 1000 Kms</option>
    <option value="within_10kms">Within 10 Kms</option>
    <option value="within_50kms">Within 50 Kms</option>
    <option value="within_100kms">Within 100 Kms</option>
    <option value="within_1000kms">Within 1000 Kms</option>
  </select>
</div>
        <br />
        {/* Price Per Quantity */}
        <div>
          <label className="pb-2">Price Per Quantity <span className="text-red-500">*</span></label>
          <select
            className="w-full mt-2 border h-[35px] rounded-[5px]"
            name="pricePerQty"
            value={formData.pricePerQty}
            onChange={handleChange}
          >
            <option value="Nos">Nos</option>
            <option value="Kilogram">Kilogram</option>
            <option value="Gram">Gram</option>
            <option value="Meter">Meter</option>
            <option value="Millimeter">Millimeter</option>
            <option value="Centimeter">Centimeter</option>
            <option value="Foot">Foot</option>
            <option value="Square foot">Square foot</option>
            <option value="Litre">Litre</option>
            <option value="Millilitre">Millilitre</option>
            <option value="Cubic meter">Cubic meter</option>
            <option value="Dozen">Dozen</option>
            <option value="Others">Others</option>

          </select>
          {formData.pricePerQty === "Others" && (
  <div className="mt-2">
    <label className="pb-2">Specify Price Per Quantity <span className="text-red-500">*</span></label>
    <input
      type="text"
      name="customPricePerQty"
      value={formData.customPricePerQty}
      className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      onChange={(e) =>
        setFormData((prevData) => ({
          ...prevData,
          customPricePerQty: e.target.value,
        }))
      }
      placeholder="Enter custom price per quantity..."
      required
    />
  </div>
)}
<div>
  <label className="pb-2">
    Minimum Order Quantity <span className="text-red-500">*</span>
  </label>
  <input
    type="number"
    name="minimumOrderQuantity"
    value={formData.minimumOrderQuantity}
    onChange={(e) =>
      setFormData((prevData) => ({
        ...prevData,
        minimumOrderQuantity: parseInt(e.target.value, 10) || 1,
      }))
    }
    className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
    min="1"
    required
    placeholder="Enter minimum order quantity"
  />
</div>
<br />
        </div>
        <br />
        {/* GST Percentage */}
        <div>
          <label className="pb-2">
            GST Percentage<span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="gstPercentage"
            value={formData.gstPercentage}
            className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product GST Percentage"
            required
          />
        </div>
        <br />
        {/* HSN Code */}
        <div>
          <label className="pb-2">
            HSN Code <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="hsnCode"
            value={formData.hsnCode}
            className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product HSN Code"
            required
          />
        </div>
        <br />
        {/* Shop Lead Time */}
        <div>
          <label className="pb-2">
            Shop Lead Time <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="leadTime"
            value={formData.leadTime}
            className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            onChange={handleChange}
            placeholder="Enter product Lead Time"
            required
          />
        </div>
        <br />
        {!hasVariants && (
  <>
  
{/* Stock Availability */}
<div>
  <label className="pb-2">
    Stock Availability <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    <label className="mr-4">
      <input
        type="radio"
        name="stockAvailability"
        value="All time stock available"
        checked={formData.stockAvailability === "All time stock available"}
        onChange={handleChange}
      />{" "}
      All time stock available
    </label>
    <label className="mr-4">
      <input
        type="radio"
        name="stockAvailability"
        value="Limited stock available"
        checked={formData.stockAvailability === "Limited stock available"}
        onChange={handleChange}
      />{" "}
      Limited stock available
    </label>
    <label>
      <input
        type="radio"
        name="stockAvailability"
        value="Out of stock"
        checked={formData.stockAvailability === "Out of stock"}
        onChange={handleChange}
      />{" "}
      Out of stock
    </label>
  </div>
</div>

{/* If Limited stock is selected, show the stock quantity input */}
{formData.stockAvailability === "Limited stock available" && (
  <div>
    <label className="pb-2">
      Stock Quantity <span className="text-red-500">*</span>
    </label>
    <input
      type="number"
      name="stock"
      value={formData.stock}
      className="mt-2 block w-full px-3 h-[35px] border border-gray-300 rounded-[3px]"
      onChange={handleChange}
      placeholder="Enter your product stock..."
    />
  </div>
)}
    <br />
  </>
)}


        {/* Product Images with Drag and Drop */}
        <div>
          <label className="pb-2">
            Product Images<span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            name="images"
            id="upload"
            className="hidden"
            multiple
            accept="image/*"
            onChange={handleNewImageChange}
          />
          <div className="w-full flex items-center flex-wrap">
            <label htmlFor="upload">
              <AiOutlinePlusCircle
                size={30}
                className="mt-3 cursor-pointer"
                color="#555"
              />
            </label>
            {/* Drag and Drop for Existing Images */}
            <DragDropContext onDragEnd={handleOnDragEndExistingImages}>
              <Droppable
                droppableId="existingImages"
                direction="horizontal"
              >
                {(provided) => (
                  <div
                    className="flex flex-wrap"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {formData.existingImages.map((img, index) => (
                      <Draggable
                        key={img.public_id}
                        draggableId={`existing-${img.public_id}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            className="relative"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <img
                              src={img.url}
                              alt={`Existing product ${index + 1}`}
                              className="h-[120px] w-[120px] object-cover m-2 rounded"
                            />
                            <AiOutlineDelete
                              size={20}
                              className="absolute top-2 right-2 bg-red-500 rounded-full cursor-pointer"
                              onClick={() =>
                                handleRemoveExistingImage(img.public_id)
                              }
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {/* Drag and Drop for New Image Previews */}
            <DragDropContext onDragEnd={handleOnDragEndNewImages}>
              <Droppable droppableId="newImages" direction="horizontal">
                {(provided) => (
                  <div
                    className="flex flex-wrap"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {formData.newImagePreviews.map((preview, index) => (
                      <Draggable
                        key={index}
                        draggableId={`new-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            className="relative"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <img
                              src={preview}
                              alt={`New product ${index + 1}`}
                              className="h-[120px] w-[120px] object-cover m-2 rounded"
                            />
                            <AiOutlineDelete
                              size={20}
                              className="absolute top-2 right-2 bg-red-500 rounded-full cursor-pointer"
                              onClick={() => handleRemoveNewImage(index)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
        <br />
        {/* Quantity Slabs */}
        <div>
          <label className="pb-2">Quantity Slabs</label>
          {formData.quantitySlabs.map((slab, index) => (
            <div key={index} className="mt-2">
              <div className="flex">
                <input
                  type="number"
                  name={`quantitySlabs.${index}.min`}
                  value={slab.min}
                  className="w-1/5 px-2 py-1 border border-gray-300 rounded-l-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  readOnly
                />
                <input
                  type="number"
                  name={`quantitySlabs.${index}.max`}
                  value={slab.max}
                  className="w-1/5 px-2 py-1 border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) =>
                    handleQuantitySlabChange(
                      index,
                      "max",
                      e.target.value
                    )
                  }
                  placeholder="Max"
                  required
                />
                <input
                  type="number"
                  name={`quantitySlabs.${index}.originalPrice`}
                  value={slab.originalPrice}
                  className="w-1/5 px-2 py-1 border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) =>
                    handleQuantitySlabChange(
                      index,
                      "originalPrice",
                      e.target.value
                    )
                  }
                  placeholder="Original Price"
                  required
                />
                <input
                  type="number"
                  name={`quantitySlabs.${index}.discountPrice`}
                  value={slab.discountPrice}
                  className="w-1/5 px-2 py-1 border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) =>
                    handleQuantitySlabChange(
                      index,
                      "discountPrice",
                      e.target.value
                    )
                  }
                  placeholder="Discount Price"
                  required
                />
                <input
                  type="number"
                  name={`quantitySlabs.${index}.totalPrice`}
                  value={slab.totalPrice}
                  className="w-1/5 px-2 py-1 border border-gray-300 rounded-r-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  readOnly
                  placeholder="Total Price"
                />
                {formData.quantitySlabs.length > 1 && (
                  <button
                    type="button"
                    className="ml-2 text-red-500"
                    onClick={() => removeQuantitySlab(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
          <button
            type="button"
            className="mt-2 text-blue-500"
            onClick={addQuantitySlab}
          >
            Add Quantity Slab
          </button>
        </div>
        <br />
        {/* Submit Button */}
        <div>
          <input
            type="submit"
            value="Update"
            className="mt-2 cursor-pointer appearance-none block w-full px-3 py-2 border border-gray-300 rounded-[3px] shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
      </form>
      </>
      )}
    </div>
  );
};

export default EditProduct;
